import {fetchJSON, contentEndpoint, element, icon} from './remote-data';
import {cachedPublicJSON} from './session-data';
import {bookmarkPage, mergeBookmarks, bookmarkDays, savedDay} from './api-data';
import bookmarkArchive from './bookmark-archive';
import {bookmarkCard, bookmarkDateLabel as dateLabel} from './bookmark-card';
import {reconcileChildren, pruneEntries, setText} from './bookmark-render';
import {currentLocale, translate} from './preferences';

export default function bookmarks(root = document, signal, restored) {
    const section = root.querySelector('[data-bookmarks]');
    if (!section) return;
    const viewport = section.querySelector('[data-bookmark-viewport], .bookmark-viewport');
    const archive = section.hasAttribute('data-bookmark-archive') ? bookmarkArchive(section, signal) : null;
    const period = section.querySelector('[data-bookmark-period]');
    const status = section.querySelector('[data-bookmark-status]');
    const more = section.querySelector('[data-bookmark-more]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let items = restored?.items || [], cursor = restored?.cursor || null, loading = false, failed = false, initialized = !!restored, settled = !!restored;
    const dayGroups = new Map(), cardsById = new Map();
    const left = card => card.getBoundingClientRect().left - viewport.getBoundingClientRect().left + viewport.scrollLeft;

    function updateState() {
        section.dataset.loading = loading ? (settled ? 'more' : 'initial') : 'false';
        status.hidden = !loading && !failed && items.length > 0;
        const key = failed ? 'bookmarksError' : (loading ? 'loadingBookmarks' : 'noBookmarks');
        status.dataset.i18n = key;
        status.textContent = translate(key);
        more.hidden = !cursor && !failed;
        more.disabled = loading;
        more.dataset.i18n = loading ? 'loading' : failed ? 'retry' : 'loadMore';
        more.textContent = translate(more.dataset.i18n);
        viewport.setAttribute('aria-busy', String(loading));
    }
    function render() {
        if (archive) { archive.render(items); updateState(); return; }
        const locale = currentLocale();
        const scroll = viewport.scrollLeft;
        const focused = document.activeElement?.dataset.bookmarkId;
        const days = bookmarkDays(items);
        const uniqueDates = days.map(group => group.day);
        const groups = days.map(({day, items: dayItems}) => {
            let group = dayGroups.get(day);
            if (!group) {
                group = element('div', 'bookmark-day');
                group.dataset.savedDate = day;
                group.setAttribute('role', 'group');
                group.setAttribute('aria-labelledby', `bookmark-date-${day}`);
                const button = element('button', 'bookmark-date');
                button.type = 'button'; button.dataset.bookmarkDate = day;
                button.id = `bookmark-date-${day}`;
                button.setAttribute('aria-controls', `bookmarks-${day}`);
                const date = element('time'); date.dateTime = day;
                button.append(date, icon(section.dataset.dateIcon, 5));
                const cards = element('div', 'bookmark-day-cards');
                cards.id = `bookmarks-${day}`;
                group.append(button, cards);
                dayGroups.set(day, group);
            }
            setText(group.querySelector('time'), dateLabel(day));
            const cards = dayItems.map(item => {
                const card = bookmarkCard(item, day, section, false, cardsById.get(item.id));
                cardsById.set(item.id, card);
                return card;
            });
            reconcileChildren(group.lastElementChild, cards);
            return group;
        });
        reconcileChildren(viewport, groups);
        pruneEntries(dayGroups, new Set(uniqueDates));
        pruneEntries(cardsById, new Set(items.map(item => item.id)));
        viewport.hidden = !items.length;
        if (items.length) {
            const newest = savedDay(items[0].saved_at);
            const month = dateLabel(newest, 'period');
            const sameMonth = uniqueDates.every(day => day.slice(0, 7) === newest.slice(0, 7));
            period.textContent = locale === 'zh-TW' ? `${sameMonth ? '' : '收藏至 '}${month}${sameMonth ? '收藏' : ''}` : `${sameMonth ? 'Saved in' : 'Saved through'} ${month}`;
            period.lang = locale;
        } else period.textContent = '';
        viewport.scrollLeft = scroll;
        if (focused) [...viewport.querySelectorAll('.bookmark-card')].find(card => card.dataset.bookmarkId === focused)?.focus({preventScroll: true});
        updateState();
    }
    async function load(fromButton = false) {
        if (loading) return;
        const previousIds = new Set(items.map(item => item.id));
        loading = true; failed = false; updateState();
        try {
            const url = contentEndpoint(section.dataset.api, '/api/v1/bookmarks');
            url.searchParams.set('limit', '15');
            if (cursor) url.searchParams.set('cursor', cursor);
            const page = cursor ? bookmarkPage(await fetchJSON(url, {signal})) : await cachedPublicJSON(url, bookmarkPage, {signal});
            if (signal?.aborted) return;
            const previousCount = items.length;
            items = mergeBookmarks(items, page.items);
            cursor = page.cursor !== cursor && (!initialized || items.length > previousCount) ? page.cursor : null;
            initialized = true;
        } catch (_) { if (signal?.aborted) return; failed = true; }
        settled = true;
        loading = false; render();
        if (archive && fromButton && !failed) {
            const firstNew = [...viewport.querySelectorAll('.bookmark-card')].find(card => !previousIds.has(card.dataset.bookmarkId));
            firstNew?.focus({preventScroll: true});
        }
    }
    viewport.addEventListener('click', event => {
        const button = event.target.closest('[data-bookmark-date]');
        if (!button) return;
        const card = button.closest('.bookmark-day').querySelector('.bookmark-card');
        if (card) { viewport.scrollTo({left: left(card) - 1, behavior: reducedMotion.matches ? 'instant' : 'smooth'}); card.focus({preventScroll: true}); }
    }, {signal});
    more.addEventListener('click', () => load(true), {signal});
    window.addEventListener('tiwb:language', render, {signal});
    if (restored) render();
    return {
        ready: !restored || restored.retry ? load() : Promise.resolve(),
        // Pending/failed initial requests are not empty successes. Keep successful
        // pages when pagination is interrupted, and resume that cursor on return.
        snapshot: () => initialized ? {items, cursor, retry: loading || failed} : null
    };
}
